import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import config from '../../config';
import { formatApiError, normalizeUploadError, readApiJson } from '../../utilities/apiErrorMessages';
import { getLanguageHeaders } from '../services/http-common';
import { authentication } from '../Auth/LoginSlice';

const backofficeBase = () => `${(config.apiRootUrl ?? config.apiUrl ?? '').replace(/\/$/, '')}/backoffice`;

const authHeaders = (token, json = true) => ({
  Accept: 'application/json',
  Authorization: `Bearer ${token}`,
  ...getLanguageHeaders(),
  ...(json ? { 'Content-Type': 'application/json' } : {}),
});

export const fetchProfile = createAsyncThunk(
  'profile/fetchProfile',
  async (token, { rejectWithValue }) => {
    try {
      const response = await fetch(`${backofficeBase()}/profile`, {
        method: 'GET',
        headers: authHeaders(token),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data ?? { message: 'Unable to load profile' });
      }

      const user = data?.user ?? data?.data ?? null;

      if (!user) {
        return rejectWithValue('Profile payload is empty');
      }

      return user;
    } catch (error) {
      return rejectWithValue(error?.message ?? 'Unexpected error');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'profile/updateProfile',
  async ({ token, values, avatarFile }, { rejectWithValue, dispatch }) => {
    try {
      const headers = authHeaders(token, false);
      delete headers['Content-Type'];

      let response;
      if (avatarFile) {
        const fd = new FormData();
        Object.entries(values ?? {}).forEach(([key, value]) => {
          if (value === undefined || value === null) {
            return;
          }
          if (typeof value === 'boolean') {
            fd.append(key, value ? '1' : '0');
            return;
          }
          fd.append(key, String(value));
        });
        fd.append('avatar', avatarFile);
        response = await fetch(`${backofficeBase()}/profile`, {
          method: 'POST',
          headers,
          body: fd,
        });
      } else {
        response = await fetch(`${backofficeBase()}/profile`, {
          method: 'PUT',
          headers: authHeaders(token),
          body: JSON.stringify(values),
        });
      }

      const data = await readApiJson(response);

      if (!response.ok) {
        return rejectWithValue(normalizeUploadError(response, data, 'Unable to update profile'));
      }

      const user = data?.user ?? data?.data ?? null;

      if (!user) {
        return rejectWithValue('Profile update returned empty payload');
      }

      dispatch(authentication({ token, user }));

      return { user, message: data?.message ?? 'Profile updated' };
    } catch (error) {
      return rejectWithValue(error?.message ?? 'Unexpected error');
    }
  }
);

export const updatePassword = createAsyncThunk(
  'profile/updatePassword',
  async ({ token, values }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${backofficeBase()}/password`, {
        method: 'PUT',
        headers: authHeaders(token),
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data ?? { message: 'Unable to update password' });
      }

      return { message: data?.message ?? 'Password updated' };
    } catch (error) {
      return rejectWithValue(error?.message ?? 'Unexpected error');
    }
  }
);

const initialState = {
  user: null,
  loading: false,
  error: null,
  successMessage: null,
};

const ProfileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearProfileError: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, action.error?.message ?? null);
      })

      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.successMessage = action.payload.message ?? 'Profile updated';
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, action.error?.message ?? null);
      })

      .addCase(updatePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updatePassword.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message ?? 'Password updated';
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, action.error?.message ?? null);
      });
  },
});

export const { clearProfileError } = ProfileSlice.actions;
export default ProfileSlice.reducer;
